import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Eyebrow from './Eyebrow';

interface DashboardFrameProps {
  eyebrow: React.ReactNode;
  title: React.ReactNode;
  /** One line under the title describing what the dashboard is for. */
  subtitle?: React.ReactNode;
  /** Right-aligned actions in the top bar (buttons, badges, etc.). */
  topBarRight?: React.ReactNode;
  /** Extras inside the top bar's left cluster, after the title block. */
  topBarLeftExtras?: React.ReactNode;
  /** Tabs strip below the top bar. */
  tabs?: React.ReactNode;
  /** Sub-row that sits between top bar and tabs (e.g. CG Boost campaign selector). */
  controlRow?: React.ReactNode;
  /** Banner/status row that sits between tabs and content (e.g. CG Boost status banner). */
  statusBanner?: React.ReactNode;
  /** Override the back link target (default: /chat). */
  backTo?: string;
  backLabel?: string;
  children: React.ReactNode;
}

const DashboardFrame: React.FC<DashboardFrameProps> = ({
  eyebrow,
  title,
  subtitle,
  topBarRight,
  topBarLeftExtras,
  tabs,
  controlRow,
  statusBanner,
  backTo = '/chat',
  backLabel = '← Back to Chat',
  children,
}) => {
  const navigate = useNavigate();
  const [backHover, setBackHover] = useState(false);

  // Split the leading arrow off the label so we can space it with flex gap
  // instead of relying on a single space character (gives a more refined
  // arrow → text rhythm without changing the public API).
  const labelMatch = backLabel.match(/^(\s*[←‹<]\s*)?(.*)$/);
  const arrow = labelMatch?.[1]?.trim() || '←';
  const labelText = (labelMatch?.[2] || backLabel).trim() || 'Back';

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <div style={styles.chromeRow}>
          <button
            onClick={() => navigate(backTo)}
            onMouseEnter={() => setBackHover(true)}
            onMouseLeave={() => setBackHover(false)}
            style={{
              ...styles.backLink,
              ...(backHover ? styles.backLinkHover : {}),
            }}
            aria-label={backLabel}
          >
            <span style={styles.backArrow} aria-hidden="true">{arrow}</span>
            <span>{labelText}</span>
          </button>
        </div>

        <div style={styles.topBarMain}>
          <div style={styles.topBarLeft}>
            <div style={styles.titleBlock}>
              <Eyebrow tone="accent">{eyebrow}</Eyebrow>
              <h1 style={styles.title}>{title}</h1>
              {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
            </div>
            {topBarLeftExtras}
          </div>
          {topBarRight && <div style={styles.topBarRight}>{topBarRight}</div>}
        </div>
      </div>

      {(controlRow || tabs) && (
        <div style={styles.controlBar}>
          {controlRow}
          {tabs && <div style={styles.tabBarWrap}>{tabs}</div>}
        </div>
      )}

      {statusBanner}

      <div style={styles.tabContent}>{children}</div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    background: 'transparent',
    fontFamily: 'var(--font-sans)',
    color: 'var(--fg-1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    minHeight: 0,
  },
  topBar: {
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 32px 20px',
    background: 'var(--bg-glass)',
    backdropFilter: 'blur(20px) saturate(140%)',
    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    borderBottom: '1px solid var(--border-subtle)',
    flexShrink: 0,
    gap: 10,
  },
  chromeRow: {
    display: 'flex',
    alignItems: 'center',
    // Negative left margin pulls the back-link's hit area outside the
    // 32px section padding so the link's text aligns with the title below.
    marginLeft: -10,
  },
  topBarMain: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  topBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  topBarRight: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-display)',
    letterSpacing: '-0.022em',
    lineHeight: 1.15,
  },
  subtitle: {
    margin: 0,
    fontSize: 13.5,
    color: 'var(--fg-3)',
    lineHeight: 1.4,
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    border: 'none',
    color: 'var(--fg-3)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: 6,
    fontFamily: 'var(--font-sans)',
    lineHeight: 1,
    transition: 'background-color 120ms ease, color 120ms ease',
  },
  backLinkHover: {
    backgroundColor: 'var(--bg-hover)',
    color: 'var(--fg-1)',
  },
  backArrow: {
    fontSize: 14,
    lineHeight: 1,
    display: 'inline-block',
    transform: 'translateY(-0.5px)',
  },
  controlBar: {
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255,255,255,0.4)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid var(--border-subtle)',
    flexShrink: 0,
  },
  tabBarWrap: {
    padding: '0 32px',
  },
  tabContent: {
    flex: 1,
    padding: '24px 32px 48px',
    overflowY: 'auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
};

export default DashboardFrame;
